import { apiRequest } from "../utils/useAPIRequest";
import constant from "../utils/constant";
import { businessStore } from "../stores";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { queryClient } from "../App";

const useGetAllSignedBusinesses = (
  signedUpBizTableQuery,
  filterQuery,
  searchQuery
) => {
  const query = useMemo(() => {
    if (searchQuery) {
      return `${signedUpBizTableQuery}&search=${searchQuery}`;
    } else if (filterQuery) {
      return `${signedUpBizTableQuery}&${filterQuery}`;
    } else {
      return signedUpBizTableQuery;
    }
  }, [filterQuery, signedUpBizTableQuery, searchQuery]);

  return useQuery({
    queryKey: ["getAllSignedBusinesses", query],
    queryFn: () => {
      return apiRequest({
        url: `/businesses?${query}`,
        method: "get",
      });
    },
    keepPreviousData: true,
  });
};
const useGetProspects = (prospectsTableQuery, filterQuery, searchQuery) => {
  const query = useMemo(() => {
    if (searchQuery) {
      return `${prospectsTableQuery}&search=${searchQuery}`;
    } else if (filterQuery) {
      return `${prospectsTableQuery}&${filterQuery}`;
    } else {
      return prospectsTableQuery;
    }
  }, [filterQuery, prospectsTableQuery, searchQuery]);

  return useQuery({
    queryKey: ["getProspects", query],
    queryFn: () => {
      return apiRequest({
        url: `/customers/admin/prospects?${query}`,
        method: "get",
      });
    },
    keepPreviousData: true,
  });
};

const getAllSignedBusinesses = async () => {
  let query;

  query = `limit=2000`;

  const { status, errorMessage, data } = await apiRequest({
    url: `/businesses?${query}`,
    method: "get",
  });

  if (status === constant.Success) {
    const { businesses, count } = data || {};

    businessStore.setSignedUpBusinesses(businesses);
    businessStore.setSignedUpBusinessesTotalCounts(count);
    return { status };
  }

  return { status, errorMessage };
};

const getSingleSignedUpBusinesses = async (id) => {
  const { status, errorMessage, data } = await apiRequest({
    url: `/businesses/business/${id}`,
    method: "get",
  });

  if (status === constant.Success) {
    const business = data || {};

    businessStore.setSingleSignedUpBusinesses(business);

    return { status };
  }

  return { status, errorMessage };
};

const getBusinessCategories = async (query = "") => {
  const { status, errorMessage, data } = await apiRequest({
    url: `/business-categories?${query}`,
    method: "get",
  });

  if (status === constant.Success) {
    const { bizCategories, totalCounts } = data || {};

    businessStore.setBusinessCategories(bizCategories);
    businessStore.setBusinessCategoriesTotalCount(totalCounts);

    return { status };
  }

  return { status, errorMessage };
};

const createBusinessCategory = async (payload) => {
  const { status, errorMessage } = await apiRequest({
    url: "/business-categories/businessCategory",
    method: "post",
    payload: payload,
  });

  if (status === constant.Success) {
    getBusinessCategories();

    return { status };
  }

  return { status, errorMessage };
};

const editBusinessCategory = async (id, payload) => {
  const { status, errorMessage } = await apiRequest({
    url: `/business-categories/businessCategory/${id}`,
    method: "put",
    payload: payload,
  });

  if (status === constant.Success) {
    getBusinessCategories();

    return { status };
  }

  return { status, errorMessage };
};

const deleteBusinessCategory = async (id) => {
  const { status, errorMessage } = await apiRequest({
    url: `/business-categories/businessCategory/${id}`,
    method: "delete",
  });

  if (status === constant.Success) {
    await getBusinessCategories();

    return { status };
  }

  return { status, errorMessage };
};

const deleteSubdomainUrl = async (business) => {
  const { status, errorMessage } = await apiRequest({
    url: `/businesses/store-front-url/${business}`,
    method: "delete",
  });

  if (status === constant.Success) {
    return { status };
  }

  return { status, errorMessage };
};

const updateBusinessStatus = async (payload) => {
  const { status, errorMessage } = await apiRequest({
    url: "/admin/action-business-status",
    method: "patch",
    payload: payload,
  });

  if (status === constant.Success) {
    await getAllSignedBusinesses();

    return { status };
  }

  return { status, errorMessage };
};
const updateBusiness = async (payload, bId) => {
  const { status, errorMessage } = await apiRequest({
    url: `/businesses/admin/business/update?business=${bId}`,
    method: "patch",
    payload: payload,
  });

  if (status === constant.Success) {
    await getAllSignedBusinesses();
    return { status };
  }

  return { status, errorMessage };
};

const getSignedUpBusinessesAnalysis = async (query) => {
  const { status, errorMessage, data } = await apiRequest({
    url: `/businesses/admin/analysis/business?${query}`,
    method: "get",
  });

  if (status === constant.Success) {
    businessStore.setSignedupBusinessesAnalysis(data);
    return { status };
  }

  return { status, errorMessage };
};

const createProspect = async (payload) => {
  const { status, errorMessage, data } = await apiRequest({
    url: "/customers/admim/prospect",
    method: "post",
    payload,
  });
  if (status === constant.Success) {
    queryClient.invalidateQueries({ queryKey: ["getProspects"] });
    return { status: constant.Success, data };
  }
  return { errorMessage, status, data };
};

const createBulkProspects = async payload => {
	const { status, errorMessage } = await apiRequest({
		url: '/customers/bulk-create-prospects',
		method: 'post',
		payload,
		hasImageUpload: true,
	});
	if (status === constant.Success) {
		const process = () =>
			new Promise(resolve =>
				setTimeout(async () => {
          queryClient.invalidateQueries({ queryKey: ["getProspects"] });
					resolve({ status });
				}, 3000)
			);
		return await process();
	}
	return { status, errorMessage };
};

const BusinessController = {
  getAllSignedBusinesses,
  getSingleSignedUpBusinesses,
  getBusinessCategories,
  createBusinessCategory,
  editBusinessCategory,
  deleteBusinessCategory,
  updateBusinessStatus,
  getSignedUpBusinessesAnalysis,
  useGetAllSignedBusinesses,
  deleteSubdomainUrl,
  useGetProspects,
  createProspect,
  updateBusiness,
  createBulkProspects
};

export default BusinessController;
